import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { PostEditor, type EditorInitial } from "@/components/blog/PostEditor";

export const Route = createFileRoute("/_admin/admin/blog/$id/edit")({
  component: EditPostRoute,
});

function EditPostRoute() {
  const { id } = Route.useParams();
  const [initial, setInitial] = useState<EditorInitial | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from("blog_posts")
          .select("*, blog_post_tags(tag_id, tag:blog_tags(id, slug, name))")
          .eq("id", id)
          .maybeSingle();

        if (fetchError) throw fetchError;
        if (cancelled) return;
        if (!data) {
          setError("Post not found");
          return;
        }
        type Row = {
          id: string;
          slug: string;
          title: string;
          excerpt: string;
          content: string;
          cover_image_url: string | null;
          cover_image_alt: string | null;
          status: "draft" | "published";
          published_at: string | null;
          author_name: string;
          meta_title: string | null;
          meta_description: string | null;
          blog_post_tags: { tag: { id: string; slug: string; name: string } | null }[] | null;
        };
        const row = data as unknown as Row;
        setInitial({
          id: row.id,
          slug: row.slug,
          title: row.title,
          excerpt: row.excerpt,
          content: row.content,
          cover_image_url: row.cover_image_url,
          cover_image_alt: row.cover_image_alt,
          status: row.status,
          published_at: row.published_at,
          author_name: row.author_name,
          meta_title: row.meta_title,
          meta_description: row.meta_description,
          tags: (row.blog_post_tags ?? [])
            .map((b) => b.tag?.name)
            .filter((n): n is string => !!n),
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (error) return <main className="mx-auto max-w-4xl px-4 py-10"><p className="text-destructive">{error}</p></main>;
  if (!initial) return <main className="mx-auto max-w-4xl px-4 py-10"><p className="text-muted-foreground">Loading…</p></main>;
  return <PostEditor mode="edit" initial={initial} />;
}
